/**
 * linkedItemSync — one item linked to one record in another system, and what
 * comes over. The routine every connector's item link uses (Linear, Jira):
 * read the record's values, run them through the connector's import mapping set
 * (Settings → Field Mappings), write what the rules say, remember what the other
 * system said, and stamp the link.
 *
 * Which value wins is the connector's Field Sync Rule for the field
 * (supabase/functions/_shared/fieldSyncRules.ts; canon 01 › Field Source, Sync
 * Direction and Conflict Rule). A field with no rule of its own takes the
 * direction of its Field Mappings rule:
 *  - **Into Priorities.ai**: the other system owns it; its value is written whenever it differs.
 *  - **Out of Priorities.ai** or **not synced**: the value here stands; the other
 *    system's value is kept as a difference and nobody is asked.
 *  - **Both ways**: a change made only there is written; a change made only here
 *    stands; a change on both sides since the last sync is settled by the field's
 *    conflict rule (Source wins, Most recent wins, or a person decides in the
 *    Conflict Queue). The value that did not win goes to the Item's History.
 *
 * What the other system last said is kept at rest in item_source_attributes
 * (migration 720); that is what makes "changed since the last sync" answerable.
 */

import { useItemsStore } from '../stores/prioritization/itemsStore';
import { useAttributesStore } from '../stores/prioritization/attributesStore';
import { useValueProposalStore } from '../stores/prioritization/valueProposalStore';
import type { Item } from '../types/prioritization/lifecycle';
import type { FieldMappingSet } from './fieldMappingRegistry';
import {
  acceptMappedAttribute,
  applyMappingSet,
  customFieldKeyOfTarget,
  internalFieldLabel,
  isBlank,
  itemCreateFieldsFromMapping,
  mappingValueNote,
  newMappingProblems,
  sameValue,
  type AcceptDirectly,
  type MappedAttributeValue,
  type MappedValue,
  type UnmatchedValue,
} from './fieldMappingApply';
import { fetchConnectorConfigsFromDB, logChangeEvent, notifyExternalIdsChanged, registerExternalId } from './connectors';
import type { Json } from '../types/database.generated';
import { getTypedSupabaseClient } from './typedSupabaseClient';
import {
  decideIncoming,
  fieldSyncRuleFor,
  ruleFromMappingDirection,
  setAsideAuditRow,
  type ConnectorDirection,
  type IncomingDecision,
  type StoredFieldRules,
} from '../../supabase/functions/_shared/fieldSyncRules';
import { listSourceAttributesForItem, recordSourceAttributes } from './semanticConnectors/unifiedAttributesService';

// ── The plan (pure) ───────────────────────────────────────────────────────────

export interface LinkedSyncDecision {
  internalFieldKey: string;
  externalFieldKey: string;
  value: unknown;
  /** review: changed on both sides and a person decides (the Conflict Queue). */
  action: 'write' | 'keep' | 'skip' | 'review';
  reason?: string;
  /** The value here, when the decision was a conflict. */
  current?: unknown;
  /** Which side's value lost a conflict a rule settled: it goes to History. */
  loser?: 'here' | 'there';
}

export interface LinkedSyncPlan {
  /** For updateItem: item fields, merged metadata, merged tags. Empty when nothing is written. */
  patch: Record<string, unknown>;
  /** Scoring-attribute values to accept (through the governed accept path). */
  attributes: MappedAttributeValue[];
  decisions: LinkedSyncDecision[];
  unmatched: UnmatchedValue[];
}

function norm(v: unknown): string {
  if (Array.isArray(v)) return v.map(norm).join('\u0000');
  return String(v ?? '').trim();
}

/**
 * Decide what this sync writes. `lastSeen` is what the other system said at the
 * last sync (null on the first). `currentAttributeValue` reads the item's
 * accepted value on a scale; without it, an attribute is written when the other
 * system's value changed since the last sync.
 */
export function planLinkedSync(params: {
  /** "Linear", "Jira" — for the reasons a field was kept. */
  systemName: string;
  values: Record<string, unknown>;
  lastSeen: Record<string, unknown> | null;
  set: FieldMappingSet | null;
  item: Pick<Item, 'title' | 'description' | 'metadata' | 'tags'> & Record<string, unknown>;
  /** Keys of the workspace's custom fields (a rule naming another one is skipped, not written blind). */
  customFieldKeys: ReadonlySet<string>;
  currentAttributeValue?: (attributeKey: string, scaleId: string | undefined) => unknown;
  /** The connector's Field Sync Rules (connector_configs.trust_rules). */
  rules?: StoredFieldRules | null;
  /** The connector's own direction. */
  connectorDirection?: ConnectorDirection;
}): LinkedSyncPlan {
  const { systemName, values, lastSeen, set, item, customFieldKeys, currentAttributeValue, rules, connectorDirection } = params;
  const applied = applyMappingSet(values, set);
  const plan: LinkedSyncPlan = { patch: {}, attributes: [], decisions: [], unmatched: applied.unmatched };

  // What both sides agreed at the last sync, in Priorities.ai's terms.
  const before = lastSeen ? applyMappingSet(lastSeen, set) : null;
  const agreed = new Map<string, unknown>();
  for (const m of [...(before?.fields ?? []), ...(before?.attributes ?? [])]) agreed.set(m.ruleId, m.value);

  const changedThere = (externalFieldKey: string): boolean =>
    lastSeen === null || norm(values[externalFieldKey]) !== norm(lastSeen[externalFieldKey]);
  const ruleOf = (mapped: MappedValue) =>
    fieldSyncRuleFor(
      rules,
      mapped.internalFieldKey,
      ruleFromMappingDirection(set?.entries.find((e) => e.id === mapped.ruleId)?.direction),
    );

  const settle = (mapped: MappedValue, current: unknown, same: boolean, value: unknown = mapped.value): LinkedSyncDecision => {
    const base = { internalFieldKey: mapped.internalFieldKey, externalFieldKey: mapped.externalFieldKey, value };
    const was = agreed.get(mapped.ruleId);
    const d: IncomingDecision = decideIncoming(
      ruleOf(mapped),
      {
        changedThere: changedThere(mapped.externalFieldKey),
        // Blank here is never an edit made here: the sync fills it.
        changedHere: was !== undefined && !isBlank(current) && !sameValue(current, was),
        same,
      },
      systemName,
      connectorDirection,
    );
    if (d.action === 'apply') return { ...base, action: 'write', ...(d.conflict ? { current, loser: d.loser, reason: d.reason } : {}) };
    if (d.action === 'review') return { ...base, action: 'review', current, reason: d.reason };
    return { ...base, action: 'keep', reason: d.reason, ...(d.conflict ? { current, loser: d.loser } : {}) };
  };

  const decide = (mapped: MappedValue, current: unknown): LinkedSyncDecision =>
    settle(mapped, current, norm(current) === norm(mapped.value));

  let metadata: Record<string, unknown> | null = null;
  let tags: string[] | null = null;

  for (const mapped of applied.fields) {
    const customFieldKey = customFieldKeyOfTarget(mapped.internalFieldKey);
    if (customFieldKey) {
      if (!customFieldKeys.has(customFieldKey)) {
        plan.decisions.push({
          internalFieldKey: mapped.internalFieldKey,
          externalFieldKey: mapped.externalFieldKey,
          value: mapped.value,
          action: 'skip',
          reason: `this workspace has no custom field “${customFieldKey}”`,
        });
        continue;
      }
      const decision = decide(mapped, item.metadata?.[customFieldKey]);
      plan.decisions.push(decision);
      if (decision.action === 'write') {
        metadata = { ...(metadata ?? item.metadata ?? {}), [customFieldKey]: mapped.value };
      }
      continue;
    }

    const create = itemCreateFieldsFromMapping([mapped]);
    const skipped = create.skipped[0];
    if (skipped) {
      plan.decisions.push({
        internalFieldKey: mapped.internalFieldKey,
        externalFieldKey: mapped.externalFieldKey,
        value: mapped.value,
        action: 'skip',
        reason: skipped.reason === 'value_not_allowed' ? `“${String(mapped.value)}” is not a value this field takes` : 'this field cannot be written by a sync',
      });
      continue;
    }
    if (create.tags.length > 0) {
      const currentTags = item.tags ?? [];
      const missing = create.tags.filter((t) => !currentTags.includes(t));
      const decision = settle(mapped, currentTags, missing.length === 0, create.tags);
      plan.decisions.push(decision);
      if (decision.action === 'write') tags = [...(tags ?? currentTags), ...missing];
      continue;
    }
    for (const [field, value] of Object.entries(create.fields)) {
      const decision = decide({ ...mapped, value }, item[field]);
      plan.decisions.push(decision);
      if (decision.action === 'write') plan.patch[field] = value;
    }
  }

  for (const mapped of applied.attributes) {
    const current = currentAttributeValue?.(mapped.attributeKey, mapped.scaleId);
    const decision = settle(mapped, current, !isBlank(current) && sameValue(current, mapped.value));
    plan.decisions.push(decision);
    if (decision.action === 'write') plan.attributes.push(mapped);
  }

  if (metadata) plan.patch.metadata = metadata;
  if (tags) plan.patch.tags = tags;
  return plan;
}

// ── Apply ─────────────────────────────────────────────────────────────────────

/** The record a sync is about — enough to label the link and stamp the sync. */
export interface LinkedRecordRef {
  /** The other system's stable id: the link's join key. */
  id: string;
  /** What a person calls it: PRI-12, SCRUM-1. */
  label: string;
  url: string;
  title: string;
  /** The other system's last-changed stamp, kept as the link's sync hash. */
  updatedAt: string;
  /** Team or project key, when the system has one. */
  projectKey?: string;
}

export interface LinkedSyncResult {
  ok: boolean;
  record?: { id: string; label: string; url: string; title: string };
  /** Field labels this sync wrote. */
  wrote: string[];
  /** Field labels left as they were, with why. */
  kept: Array<{ label: string; reason: string }>;
  /** Rules that could not run, with why. */
  skipped: Array<{ label: string; reason: string }>;
  errors: string[];
}

function mappedFieldLabel(internalFieldKey: string): string {
  const attributes = useAttributesStore.getState().attributes;
  return internalFieldLabel(internalFieldKey, (k) => attributes.find((a) => a.key === k)?.label);
}

/** The item's official value of an attribute on a scale (no scale = the unscaled value). */
export function currentOfficialValue(itemId: string, attributeKey: string, scaleId: string | undefined): unknown {
  const values = useValueProposalStore.getState().acceptedValues;
  return values.find(
    (v) => v.itemId === itemId && v.attributeKey === attributeKey && !v.sessionScope && (v.scaleId ?? undefined) === scaleId,
  )?.value;
}

/**
 * Bring one record's values (already read) onto one item through the
 * connector's mapping set, record what the other system said, and stamp the link.
 */
export async function applyLinkedValuesToItem(params: {
  connectorType: string;
  systemName: string;
  workspaceId: string;
  itemId: string;
  userId?: string | null;
  /** Why this sync ran: link, now, auto, import. */
  reason: string;
  record: LinkedRecordRef;
  values: Record<string, unknown>;
  set: FieldMappingSet;
}): Promise<LinkedSyncResult> {
  const { connectorType, systemName, workspaceId, itemId, userId, reason, record, values, set } = params;
  const { data: configs } = await fetchConnectorConfigsFromDB(workspaceId);
  const config = configs.find((c) => c.connectorType === connectorType && c.userId === null);
  const result: LinkedSyncResult = { ok: false, wrote: [], kept: [], skipped: [], errors: [] };
  result.record = { id: record.id, label: record.label, url: record.url, title: record.title };

  const itemsStore = useItemsStore.getState();
  const item = itemsStore.getItemById(itemId);
  if (!item) {
    result.errors.push('The item is not loaded');
    return result;
  }

  const previous = await listSourceAttributesForItem(workspaceId, itemId).catch(() => []);
  const rows = previous.filter((r) => r.connectorType === connectorType && r.sourceKind === 'field');
  const lastSeen = rows.length > 0 ? Object.fromEntries(rows.map((r) => [r.sourceKey, r.rawValue])) : null;

  const customFieldKeys = new Set(
    useAttributesStore.getState().attributes.filter((a) => a.isCustomField === true).map((a) => a.key),
  );
  const plan = planLinkedSync({
    systemName,
    values,
    lastSeen,
    set,
    item: item as unknown as Pick<Item, 'title' | 'description' | 'metadata' | 'tags'> & Record<string, unknown>,
    customFieldKeys,
    currentAttributeValue: (key, scaleId) => currentOfficialValue(itemId, key, scaleId),
    rules: config?.trustRules ?? null,
    connectorDirection: config?.syncDirection,
  });

  for (const d of plan.decisions) {
    if (d.action === 'keep') result.kept.push({ label: mappedFieldLabel(d.internalFieldKey), reason: d.reason ?? '' });
    if (d.action === 'skip') result.skipped.push({ label: mappedFieldLabel(d.internalFieldKey), reason: d.reason ?? '' });
  }
  for (const u of plan.unmatched) {
    result.skipped.push({ label: mappedFieldLabel(u.internalFieldKey), reason: `“${String(u.externalValue)}” is not in the rule's value list` });
  }

  // Changed on both sides, and a person decides: the Conflict Queue. The value here stands until then.
  const supabase = getTypedSupabaseClient();
  for (const d of plan.decisions.filter((x) => x.action === 'review')) {
    result.kept.push({ label: mappedFieldLabel(d.internalFieldKey), reason: d.reason ?? '' });
    const { error } = await supabase.from('change_events').insert({
      workspace_id: workspaceId,
      source: connectorType,
      entity_type: 'item',
      entity_id: itemId,
      field_name: d.internalFieldKey,
      old_value: (d.current ?? null) as Json,
      new_value: (d.value ?? null) as Json,
      event_type: 'updated',
      sync_status: 'conflicted',
      connector_config_id: config?.id ?? null,
      conflict_data: {
        trustDecision: 'negotiate',
        incomingSource: connectorType,
        field: d.internalFieldKey,
        incomingValue: d.value,
        localValue: d.current,
        reason: d.reason,
        detectedAt: new Date().toISOString(),
      } as Json,
    });
    if (error) result.errors.push(`The conflict on ${mappedFieldLabel(d.internalFieldKey)} was not queued: ${error.message}`);
  }

  // The value that did not win a conflict is kept in the Item's History.
  for (const d of plan.decisions.filter((x) => x.loser)) {
    const { error } = await supabase.from('audit_events').insert(
      setAsideAuditRow({
        workspaceId,
        itemId,
        field: d.internalFieldKey,
        value: d.loser === 'there' ? d.value : d.current,
        from: d.loser === 'there' ? systemName : 'Priorities.ai',
        systemName,
        reason: d.reason ?? '',
      }) as never,
    );
    if (error) result.errors.push(`The value set aside on ${mappedFieldLabel(d.internalFieldKey)} was not kept: ${error.message}`);
  }

  if (Object.keys(plan.patch).length > 0) {
    const outcome = await itemsStore.updateItem(itemId, plan.patch as Partial<Item>);
    if (!outcome.ok) {
      result.errors.push(`The item was not updated: ${outcome.reason ?? 'refused'}`);
    } else {
      for (const d of plan.decisions) {
        if (d.action === 'write' && !d.internalFieldKey.startsWith('attribute:')) result.wrote.push(mappedFieldLabel(d.internalFieldKey));
      }
    }
  }

  if (plan.attributes.length > 0) {
    const problems = newMappingProblems();
    const deps = {
      acceptedBy: userId ?? null,
      accept: ((p) => useValueProposalStore.getState().acceptValueDirectly(p)) as AcceptDirectly,
    };
    for (const mapped of plan.attributes) {
      const saved = await acceptMappedAttribute(
        deps,
        itemId,
        mapped,
        mappingValueNote(`at ${systemName} sync of ${record.label}`, connectorType, mapped, set.name),
        problems,
      );
      if (saved) result.wrote.push(mappedFieldLabel(mapped.internalFieldKey));
    }
    if (problems.failed > 0) result.errors.push(problems.firstFailure || 'A value was not saved');
  }

  // What the other system said, at rest — the next sync's "did it change" baseline.
  const recorded = await recordSourceAttributes({
    workspaceId,
    connectorType,
    origin: 'sync',
    rows: Object.entries(values).map(([sourceKey, rawValue]) => ({
      itemId,
      externalId: record.id,
      sourceKind: 'field' as const,
      sourceKey,
      rawValue,
    })),
  });
  if (recorded.error) result.errors.push(recorded.error);

  const link = await registerExternalId({
    workspaceId,
    entityType: 'item',
    internalId: itemId,
    connectorType,
    externalId: record.id,
    externalLabel: record.label,
    externalUrl: record.url || undefined,
    externalProjectKey: record.projectKey,
    syncHash: record.updatedAt,
  });
  if (link.error) result.errors.push(`The link to ${record.label} was not saved: ${link.error}`);
  void logChangeEvent({
    workspaceId,
    entityType: 'item',
    entityId: itemId,
    eventType: 'synced',
    fieldName: connectorType,
    newValue: { record: record.label, reason, wrote: result.wrote, kept: result.kept.map((k) => k.label) },
    syncStatus: 'synced',
  });
  notifyExternalIdsChanged('item', itemId);

  result.wrote = [...new Set(result.wrote)];
  result.ok = result.errors.length === 0;
  return result;
}

/** One line for a toast or a panel: "Brought over Impact and Effort from SCRUM-1." */
export function describeLinkedSync(result: LinkedSyncResult, fallbackLabel: string): string {
  const key = result.record?.label ?? fallbackLabel;
  if (!result.ok && result.errors.length > 0) return `Sync with ${key} did not finish: ${result.errors[0]}`;
  if (result.wrote.length === 0) return `Nothing new from ${key} — the item already matches it.`;
  const list = result.wrote.length === 1 ? result.wrote[0] : `${result.wrote.slice(0, -1).join(', ')} and ${result.wrote[result.wrote.length - 1]}`;
  return `Brought over ${list} from ${key}.`;
}
